import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { PageIntro } from "../components/PageIntro";
import { Reveal } from "../components/Reveal";
import { POLICIES, POLICY_NAV, COMPANY } from "../lib/policies";
import { waLink } from "../lib/data";

const Block = ({ block }) => {
  if (block.type === "h3") {
    return (
      <h3 className="font-serif text-2xl md:text-3xl text-brand-ink mt-12 mb-4 first:mt-0">
        {block.text}
      </h3>
    );
  }
  if (block.type === "ul") {
    return (
      <ul className="my-4 space-y-3">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-brand-muted leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-sprout shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-brand-muted leading-relaxed mb-4">{block.text}</p>;
};

export default function Legal({ slug }) {
  const policy = POLICIES[slug];
  if (!policy) return null;

  return (
    <div data-testid="legal-page">
      <PageIntro
        label="Legal"
        titleLines={[policy.title]}
        subtitle={`Last updated: ${policy.updated}`}
      />

      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          {/* Sub-nav */}
          <aside className="md:col-span-3">
            <div className="md:sticky md:top-28">
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-5">
                Policies
              </p>
              <nav className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
                {POLICY_NAV.map((p) => {
                  const active = p.slug === slug;
                  return (
                    <Link
                      key={p.slug}
                      to={p.path}
                      data-testid={`policy-nav-${p.slug}`}
                      className={`whitespace-nowrap rounded-full md:rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                        active
                          ? "bg-brand-forest text-brand-bg"
                          : "text-brand-muted hover:text-brand-ink hover:bg-brand-card"
                      }`}
                    >
                      {p.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="md:col-span-9 lg:col-span-8">
            <Reveal>
              <article className="max-w-3xl">
                {policy.blocks.map((block, i) => (
                  <Block key={i} block={block} />
                ))}

                {/* Company card */}
                <div className="mt-14 rounded-[1.5rem] bg-brand-card border border-black/5 p-8">
                  <p className="font-serif text-xl text-brand-ink">{COMPANY.name}</p>
                  <div className="mt-4 space-y-2 text-sm text-brand-muted">
                    <p className="flex items-start gap-2">
                      <MapPin size={15} className="mt-0.5 shrink-0" /> {COMPANY.address}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail size={15} />
                      <a href={`mailto:${COMPANY.email}`} className="hover:text-brand-forest transition-colors">
                        {COMPANY.email}
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone size={15} />
                      <a href={waLink("Hi 40X Leaves, ")} target="_blank" rel="noopener noreferrer" className="hover:text-brand-forest transition-colors">
                        Phone / WhatsApp: {COMPANY.phone}
                      </a>
                    </p>
                  </div>
                  <a
                    href={waLink("Hi 40X Leaves, I have a question about your policies. ")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="legal-whatsapp-cta"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-forest text-brand-bg px-6 py-3 text-sm font-semibold transition-transform duration-300 hover:scale-105 hover:bg-brand-sprout"
                  >
                    Ask us on WhatsApp <ArrowUpRight size={16} />
                  </a>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
